/**
 * Billing, "Tarify". Vysvětluje podrobně, co zákazník za každou variantu dostane.
 * Nákup přes Stripe; FREE rovnou zahájí valuaci. Když platby nejsou zapnuté, zobrazí info.
 */
import React, { useEffect, useState } from 'react';
import { getBillingStatus, startCheckout } from '../lib/apiFetch';

// Číselné ceny pro výpočet věrnostní slevy
const PRICE_CZK = { pro: 6990, audit: 4990, komplet: 9990 };
const fmtCzk = n => n.toLocaleString('cs-CZ').replace(/ /g, ' ') + ' Kč';

const TIERS = [
  {
    key: 'pro', name: 'PRO valuace', price: '6 990 Kč', buyable: true, badge: 'Oblíbené',
    tagline: 'Pochopte, PROČ má firma takovou hodnotu a JAK ji zvýšit.',
    forWhom: 'Pro majitele, který se připravuje na prodej nebo jednání s investorem.',
    features: [
      ['Vše ze Základní valuace', 'Hodnota, rozsah a klíčové metriky.'],
      ['Finanční zdraví (ROA, ROE, ROS)', 'Jak zdravě firma hospodaří ve srovnání s oborem.'],
      ['Business Quality Score (8 dimenzí)', 'Skóre kvality byznysu, co táhne hodnotu nahoru a co dolů.'],
      ['Jak zvýšit hodnotu, v Kč', 'Konkrétní páky a o kolik korun každá zvedne cenu firmy.'],
      ['Rizikové faktory a simulace růstu', 'Co kupce odradí a jak by vypadala hodnota v různých scénářích.'],
      ['Expertní výkladový report', 'Každá metrika lidsky vysvětlená: co znamená, proč na ní záleží, jak ji zlepšit.'],
      ['Pohled investora + Exit Readiness', 'Investiční známka (A+ až D) a jak je firma připravená na prodej.'],
    ],
  },
  {
    key: 'audit', name: 'Audit podniku', price: '4 990 Kč', buyable: true,
    tagline: 'Zjistěte, KDE ztrácíte zisk a co s tím.',
    forWhom: 'Pro majitele, který chce firmu zlepšit a vydělat víc.',
    features: [
      ['Analýza ztraceného zisku', 'Kolik korun ročně necháváte na stole a kde.'],
      ['6 audit modulů', 'Marže, retence zákazníků, reklama, sklady, akvizice, konverze.'],
      ['Prioritní akce podle dopadu', 'Co řešit první, seřazené podle toho, kolik to přinese.'],
      ['Konzultantský report + SWOT', 'Silné a slabé stránky, příležitosti a hrozby vašeho podniku.'],
      ['Detailní průvodce krok za krokem', 'Jak každou akci konkrétně provést, ne jen obecné rady.'],
    ],
  },
  {
    key: 'komplet', name: 'Kompletní řešení', price: '9 990 Kč', buyable: true, highlight: true, badge: 'Nejlepší volba',
    tagline: 'PRO valuace + Audit dohromady. Ušetříte 1 990 Kč.',
    forWhom: 'Pro maximum, plný obraz hodnoty i toho, jak ji zvýšit.',
    features: [
      ['Vše z PRO valuace', 'Hodnota, report, pohled investora, Exit Readiness.'],
      ['Vše z Auditu', 'Ztracený zisk, moduly, prioritní akce, SWOT.'],
      ['Kompletní podklady pro prodej', 'Vše, co potřebujete k jednání s kupcem nebo investorem.'],
    ],
  },
  {
    key: 'free', name: 'Základní valuace', price: 'Zdarma', buyable: false,
    tagline: 'Zjistěte, kolik vaše firma zhruba stojí.',
    forWhom: 'Pro první představu o hodnotě podniku.',
    features: [
      ['Odhadovaná tržní hodnota firmy', 'Jedno hlavní číslo, kolik by za vaši firmu kupující dal.'],
      ['Konzervativní a optimistický rozsah', 'Od, do, abyste věděli, v jakém pásmu se cena pohybuje.'],
      ['6 klíčových metrik', 'EBITDA, marže, růst, retence a další, s barevným hodnocením.'],
    ],
  },
];

export function Billing({ onStartValuation, isStaff = false }) {
  const [enabled, setEnabled] = useState(null);
  const [loyalty, setLoyalty] = useState(false);
  const [loyaltyPct, setLoyaltyPct] = useState(30);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getBillingStatus()
      .then(c => { setEnabled(!!c.enabled); setLoyalty(!!c.loyalty); setLoyaltyPct(c.loyalty_percent || 30); })
      .catch(() => setEnabled(false));
  }, []);

  const buy = async (tier) => {
    setBusy(tier); setError(null);
    try { await startCheckout(tier); }
    catch (e) { setError(e.message); setBusy(null); }
  };

  return (
    <div style={{ maxWidth: 1040, margin: '0 auto', padding: '1.5rem 1rem', fontFamily: "'Inter', sans-serif" }}>
      <h2 style={{ marginTop: 0, fontFamily: "'Source Serif 4', Georgia, serif", color: 'var(--star-white)', fontSize: '1.4rem' }}>
        Tarify
      </h2>
      <p style={{ color: 'var(--text-dim)', maxWidth: 680, marginBottom: '1.75rem', lineHeight: 1.55 }}>
        Základní valuaci máte zdarma, ukáže vám hodnotu firmy. Placené varianty jdou hlouběji:
        vysvětlí, proč ta hodnota vyšla a jak ji zvýšit (PRO), nebo kde ztrácíte zisk (Audit).
        Platí se jednorázově za konkrétní analýzu, žádné předplatné.
      </p>

      {isStaff && (
        <div style={{ padding: '0.85rem 1rem', borderRadius: 10, background: 'rgba(91,59,140,0.08)',
          border: '1px solid rgba(91,59,140,0.3)', color: 'var(--purple-glow)', marginBottom: '1.5rem', fontSize: '0.9rem', fontWeight: 600 }}>
          Jako interní účet (tým / admin) máte všechny analýzy zdarma. Stačí spustit analýzu, report
          se vygeneruje bez platby. Ceny níže jsou orientační, platí pro klienty.
        </div>
      )}
      {enabled === false && !isStaff && (
        <div style={{ padding: '0.85rem 1rem', borderRadius: 10, background: 'rgba(160,123,45,0.08)',
          border: '1px solid rgba(160,123,45,0.3)', color: 'var(--gold)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
          Online platby zatím nejsou aktivní. Základní valuaci ale můžete spustit hned zdarma.
        </div>
      )}
      {loyalty && !isStaff && (
        <div style={{ padding: '0.85rem 1rem', borderRadius: 10, background: 'rgba(46,158,91,0.09)',
          border: '1px solid rgba(46,158,91,0.35)', color: 'var(--color-success)', marginBottom: '1.5rem', fontSize: '0.9rem', fontWeight: 600 }}>
          Jako vracejícímu se klientovi vám u každého dalšího nákupu automaticky strhneme {loyaltyPct} % z ceny.
          Slevu uvidíte i v platební bráně.
        </div>
      )}
      {error && <div style={{ color: 'var(--color-danger)', marginBottom: '1rem' }}>Chyba: {error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.1rem', alignItems: 'stretch' }}>
        {TIERS.map(t => (
          <div key={t.key} style={{
            position: 'relative',
            border: t.highlight ? '2.5px solid var(--purple-glow)' : '1px solid rgba(0,0,0,0.1)',
            borderRadius: 14, padding: t.highlight ? '1.7rem 1.2rem 1.4rem' : '1.4rem 1.2rem', background: 'var(--purple-dark)',
            display: 'flex', flexDirection: 'column', gap: '0.55rem',
            boxShadow: t.highlight ? '0 10px 30px rgba(91,59,140,0.18)' : '0 1px 4px rgba(0,0,0,0.06)',
            transform: t.highlight ? 'translateY(-4px)' : 'none',
          }}>
            {t.badge && (
              <span style={{
                position: 'absolute', top: t.highlight ? -13 : -11, left: '50%', transform: 'translateX(-50%)',
                background: t.highlight ? 'var(--btn-bg)' : 'var(--purple-deep)',
                color: t.highlight ? '#fff' : 'var(--purple-glow)', fontWeight: 800, fontSize: '0.72rem',
                padding: '0.28rem 0.85rem', borderRadius: 999, whiteSpace: 'nowrap',
                boxShadow: t.highlight ? '0 4px 12px rgba(91,59,140,0.35)' : 'none', letterSpacing: '0.02em',
              }}>{t.badge}</span>
            )}
            <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '1.05rem', color: 'var(--star-white)' }}>{t.name}</div>
            {loyalty && !isStaff && t.buyable && PRICE_CZK[t.key] ? (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--color-success)' }}>
                  {fmtCzk(Math.round(PRICE_CZK[t.key] * (1 - loyaltyPct / 100)))}
                </span>
                <span style={{ fontSize: '0.9rem', color: 'var(--text-dim)', textDecoration: 'line-through' }}>{t.price}</span>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-success)', background: 'rgba(46,158,91,0.12)', padding: '0.1rem 0.4rem', borderRadius: 6 }}>−{loyaltyPct} %</span>
              </div>
            ) : (
              <div style={{ fontSize: '1.3rem', fontWeight: 800, color: t.key === 'free' ? 'var(--color-success)' : 'var(--purple-glow)' }}>{t.price}</div>
            )}
            <div style={{ fontSize: '0.86rem', color: 'var(--text-dim)', lineHeight: 1.45, fontWeight: 600 }}>{t.tagline}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', lineHeight: 1.4 }}>{t.forWhom}</div>

            <div style={{ borderTop: '1px solid var(--purple-mid)', margin: '0.3rem 0', paddingTop: '0.3rem' }} />
            <ul style={{ margin: 0, paddingLeft: '1.15rem', fontSize: '0.82rem', color: 'var(--text-dim)', lineHeight: 1.45, flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {t.features.map(([h, d], i) => (
                <li key={i}>
                  <span style={{ fontWeight: 600, color: 'var(--star-white)' }}>{h}</span>
                  <span style={{ display: 'block', color: 'var(--text-dim)' }}>{d}</span>
                </li>
              ))}
            </ul>

            {isStaff ? (
              // Interní účet (tým/admin): negeneruje platbu, jen spustí analýzu (zdarma)
              <button onClick={onStartValuation} style={{
                ...btn, background: t.highlight ? 'var(--btn-bg)' : (t.key === 'free' ? 'var(--color-success)' : 'var(--purple-glow)'),
              }}>
                Zahájit analýzu (zdarma)
              </button>
            ) : t.key === 'free' ? (
              <button onClick={onStartValuation} style={{ ...btn, background: 'var(--color-success)' }}>Zahájit valuaci</button>
            ) : (
              <button onClick={() => buy(t.key)} disabled={!enabled || busy === t.key} style={{
                ...btn,
                background: !enabled ? 'rgba(91,59,140,0.25)' : (t.highlight ? 'var(--btn-bg)' : 'var(--purple-glow)'),
                cursor: enabled ? 'pointer' : 'not-allowed', opacity: (!enabled || busy === t.key) ? 0.7 : 1,
              }}>
                {busy === t.key ? '…' : (t.highlight ? 'Vybrat nejlepší volbu' : 'Koupit')}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const btn = {
  width: '100%', padding: '0.65rem', borderRadius: 8, border: 'none', color: 'white',
  fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.03em', cursor: 'pointer', marginTop: '0.4rem',
};
